/**
 * @file api.ts
 * @brief TanStack Query hooks for platform-wide (super-admin) invoices.
 *
 * @details
 * Reads the same `GET /api/invoices` endpoint the company panel uses. The
 * backend returns a platform-wide feed for superadmin callers, so no separate
 * `/api/admin/invoices` route is needed — the tenant narrowing is a
 * `?company_id=` query param handled server-side.
 *
 * @dependencies
 * - @tanstack/react-query: Cached list query
 * - shared/api/client: apiGet
 * - shared/api/endpoints: invoiceEndpoints (URL registry)
 *
 * @references
 * - BACKEND/src/features/invoices/routes.js (GET / superadmin branch, /export)
 * - WEBPANEL/src/features/company-invoices/api.ts (company-scoped sibling)
 *
 * @author DES
 * @date 08-07-2026
 */

import {
  useQueries,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { invoiceEndpoints } from "@/shared/api/endpoints";
import {
  AdminInvoicesResponseSchema,
  invoicePeriodRange,
  type AdminInvoicesListParams,
  type AdminInvoicesResponse,
} from "./schemas";

export const adminInvoicesKeys = {
  all: ["admin", "invoices"] as const,
  list: (params: AdminInvoicesListParams) =>
    ["admin", "invoices", "list", params] as const,
};

function buildQuery(params: AdminInvoicesListParams): string {
  const usp = new URLSearchParams();
  usp.set("page", String(params.page ?? 1));
  usp.set("pageSize", String(params.pageSize ?? 25));
  if (params.q) usp.set("q", params.q);
  if (params.company_id) usp.set("company_id", params.company_id);

  if (params.from) {
    usp.set("from", params.from);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.from) usp.set("from", range.from);
  }

  if (params.to) {
    usp.set("to", params.to);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.to) usp.set("to", range.to);
  }

  return usp.toString();
}

async function fetchAdminInvoicesPage(
  params: AdminInvoicesListParams,
): Promise<AdminInvoicesResponse> {
  const raw = await apiGet<unknown>(
    `${invoiceEndpoints.list}?${buildQuery(params)}`,
  );
  return AdminInvoicesResponseSchema.parse(raw);
}

/** The list filters without paging (shared by the grouped calls). */
function filterQuery(params: AdminInvoicesListParams): URLSearchParams {
  const usp = new URLSearchParams(buildQuery(params));
  usp.delete("page");
  usp.delete("pageSize");
  return usp;
}

/**
 * One (company, owner) pair in the grouped list (`?group=company_owner`,
 * DES 01-10-2026: "haan superadmin pages pe bhi yehi kar do").
 */
export const InvoicesCompanyOwnerSummarySchema = z.object({
  company_id: z.string().nullable(),
  company_name: z.string().nullable(),
  owner_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  count: z.number(),
});
export type InvoicesCompanyOwnerSummary = z.infer<typeof InvoicesCompanyOwnerSummarySchema>;

/** Group keys: a company (`none` = Personal) and its owner leaf. */
export const invoiceCompanyGroupKey = (companyId: string | null) => companyId ?? "none";
export const invoiceCompanyOwnerGroupKey = (companyId: string | null, ownerId: string | null) =>
  `${invoiceCompanyGroupKey(companyId)}/${ownerId ?? "none"}`;

/**
 * Every (company, owner) pair with invoices under the current filters, with
 * counts. One server GROUP BY, so every company and user shows, not just the
 * ones on the loaded rows.
 */
export function useAdminInvoicesGroupSummary(params: AdminInvoicesListParams, enabled: boolean) {
  return useQuery({
    queryKey: [...adminInvoicesKeys.all, "groups", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "company_owner");
      const raw = await apiGet<{ groups?: unknown }>(`${invoiceEndpoints.list}?${usp}`);
      return z.array(InvoicesCompanyOwnerSummarySchema).parse(raw.groups ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const ADMIN_INVOICE_GROUP_BATCH = 25;

/**
 * The invoices of each OPEN leaf group (`company/owner` key), `batches`
 * pages of ADMIN_INVOICE_GROUP_BATCH under the same filters
 * (`?company=&owner=`). The server caps `pageSize` at 100, so the pages are
 * fetched and joined, as on the company Invoices page.
 */
export function useAdminInvoicesGroupRows(
  params: AdminInvoicesListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [...adminInvoicesKeys.all, "group", key, batches, filterQuery(params).toString()],
      placeholderData: (prev: AdminInvoicesResponse | undefined) => prev,
      queryFn: async (): Promise<AdminInvoicesResponse> => {
        const [company, owner] = key.split("/");
        const pages = await Promise.all(
          Array.from({ length: batches }, async (_, i) => {
            const usp = filterQuery(params);
            usp.set("company", company ?? "none");
            usp.set("owner", owner ?? "none");
            usp.set("page", String(i + 1));
            usp.set("pageSize", String(ADMIN_INVOICE_GROUP_BATCH));
            const raw = await apiGet<unknown>(`${invoiceEndpoints.list}?${usp}`);
            return AdminInvoicesResponseSchema.parse(raw);
          }),
        );
        return { ...pages[0], items: pages.flatMap((p) => p.items ?? []) };
      },
    })),
  });
}

export function useAdminInvoicesList(
  params: AdminInvoicesListParams,
  options?: Partial<UseQueryOptions<AdminInvoicesResponse>>,
) {
  return useQuery<AdminInvoicesResponse>({
    queryKey: adminInvoicesKeys.list(params),
    queryFn: () => fetchAdminInvoicesPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

export function adminInvoicesExportUrl(
  params: Pick<AdminInvoicesListParams, "from" | "to" | "company_id" | "period">,
): string {
  const usp = new URLSearchParams();
  if (params.company_id) usp.set("company_id", params.company_id);
  if (params.from) {
    usp.set("from", params.from);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.from) usp.set("from", range.from);
  }
  if (params.to) {
    usp.set("to", params.to);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.to) usp.set("to", range.to);
  }
  const qs = usp.toString();
  return qs ? `${invoiceEndpoints.export}?${qs}` : invoiceEndpoints.export;
}
